import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * PinDetailsPopover — Phase 2 optional pin metadata.
 *
 * Positioning strategy (avoids off-screen + pin-overlap bugs):
 *  - Anchor the popover to the pin's ABSOLUTE pixel position inside the stage.
 *  - Measure the popover after mount, then CLAMP the position so it stays
 *    fully inside the stage rectangle (8px gutter).
 *  - Default = below the pin. If clipped at the bottom, flip to above.
 *  - Horizontal centring is clamped against the stage's left + right edges,
 *    so a pin at x=0.05 won't push the popover off-screen.
 */

const SURFACE_OPTIONS = [
  { id: "wardrobe-shutter", label: "Wardrobe" },
  { id: "drawer-front", label: "Drawer" },
  { id: "kitchen-upper", label: "Kitchen upper" },
  { id: "kitchen-lower", label: "Kitchen lower" },
  { id: "tv-back-wall", label: "TV wall" },
  { id: "bed-back", label: "Bed back" },
  { id: "ceiling-drop", label: "Ceiling drop" },
  { id: "door", label: "Door" },
  { id: "wall-panel", label: "Wall panel" },
];

const TREATMENT_OPTIONS = [
  { id: "vertical-fluted", label: "Vertical fluted" },
  { id: "horizontal-fluted", label: "Horizontal fluted" },
  { id: "shaker-frame", label: "Shaker frame" },
  { id: "flat-shadow-gap", label: "Flat + gap" },
  { id: "bookmatched", label: "Bookmatched" },
  { id: "brass-inlay", label: "Brass inlay" },
];

export default function PinDetailsPopover({
  pin,
  letterColor,
  onUpdate,
  onRemove,
  onClose,
  stageRef,   // ref to the site image stage div — used for absolute pixel positioning
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, opacity: 0 });

  // Compute the safe popover position inside the stage rectangle.
  // Runs after the popover is rendered so we can measure its real size.
  useLayoutEffect(() => {
    if (!stageRef?.current || !ref.current) return;
    const stage = stageRef.current.getBoundingClientRect();
    const pop = ref.current.getBoundingClientRect();
    const GUTTER = 8;
    const PIN_RADIUS = 18;

    // Pin pixel coords inside the stage
    const pinX = pin.x * stage.width;
    const pinY = pin.y * stage.height;

    // Horizontal: centre on pin, clamp to stage edges
    let left = pinX - pop.width / 2;
    left = Math.max(GUTTER, Math.min(left, stage.width - pop.width - GUTTER));

    // Vertical: prefer below, fall back to above if below would clip the bottom
    const roomBelow = stage.height - pinY - PIN_RADIUS - GUTTER;
    let top;
    if (pop.height + GUTTER <= roomBelow) {
      // Place below pin
      top = pinY + PIN_RADIUS + 6;
    } else {
      // Place above pin (also clamp top)
      top = pinY - PIN_RADIUS - pop.height - 6;
      top = Math.max(GUTTER, top);
    }

    setPos({ left, top, opacity: 1 });
  }, [pin.x, pin.y, stageRef]);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    // Defer attaching so the click that opened the popover doesn't immediately close it
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onClick);
      document.addEventListener("touchstart", onClick);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const update = (patch) => onUpdate({ ...pin, ...patch });

  return (
    <div
      ref={ref}
      data-testid={`pin-popover-${pin.letter}-${pin.id}`}
      onClick={(e) => e.stopPropagation()}
      className="absolute z-40 w-[230px] max-w-[calc(100vw-24px)] rounded-xl bg-white text-heda-ink shadow-2xl ring-1 ring-heda-line"
      style={{
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        opacity: pos.opacity,
        transition: "opacity 80ms ease-out",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-heda-line/70 px-3 py-2">
        <span
          className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{ backgroundColor: letterColor.bg }}
        >
          {pin.letter}
        </span>
        <span className="text-[11px] font-medium uppercase tracking-wider text-heda-mute">
          Pin details · optional
        </span>
        <button
          type="button"
          data-testid={`pin-popover-close-${pin.id}`}
          onClick={onClose}
          className="ml-auto flex h-5 w-5 items-center justify-center rounded-full text-heda-mute hover:bg-heda-cream hover:text-heda-ink"
        >
          <X size={12} strokeWidth={2.6} />
        </button>
      </div>

      {/* Surface section */}
      <div className="px-3 pt-2.5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-heda-mute">
          Surface
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          <Chip
            label="Auto"
            active={!pin.surface_type}
            onClick={() => update({ surface_type: null })}
            testId={`surface-auto-${pin.id}`}
          />
          {SURFACE_OPTIONS.map((s) => (
            <Chip
              key={s.id}
              label={s.label}
              active={pin.surface_type === s.id}
              onClick={() =>
                update({ surface_type: pin.surface_type === s.id ? null : s.id })
              }
              testId={`surface-${s.id}-${pin.id}`}
            />
          ))}
        </div>
      </div>

      {/* Treatment section */}
      <div className="px-3 pt-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-heda-mute">
          Treatment
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          <Chip
            label="Auto"
            active={!pin.treatment}
            onClick={() => update({ treatment: null })}
            testId={`treatment-auto-${pin.id}`}
          />
          {TREATMENT_OPTIONS.map((t) => (
            <Chip
              key={t.id}
              label={t.label}
              active={pin.treatment === t.id}
              onClick={() =>
                update({ treatment: pin.treatment === t.id ? null : t.id })
              }
              testId={`treatment-${t.id}-${pin.id}`}
            />
          ))}
        </div>
      </div>

      {/* Open fixture toggle */}
      <button
        type="button"
        data-testid={`pin-openfix-${pin.id}`}
        onClick={() => update({ is_open_fixture: !pin.is_open_fixture })}
        className={[
          "mt-3 flex w-full items-center gap-2 border-t border-heda-line/70 px-3 py-2.5 text-left text-[11px] font-medium",
          pin.is_open_fixture
            ? "bg-heda-gold/8 text-heda-gold"
            : "text-heda-mute hover:bg-heda-cream/60 hover:text-heda-ink",
        ].join(" ")}
      >
        <span
          className={[
            "flex h-4 w-4 items-center justify-center rounded border",
            pin.is_open_fixture
              ? "border-heda-gold bg-heda-gold text-white"
              : "border-heda-line bg-white",
          ].join(" ")}
        >
          {pin.is_open_fixture && (
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5">
              <path
                d="M2 6.5 L5 9 L10 3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        Fixture is open / unfinished
      </button>

      {/* Remove pin */}
      <button
        type="button"
        data-testid={`pin-remove-${pin.id}`}
        onClick={onRemove}
        className="flex w-full items-center justify-center gap-1.5 border-t border-heda-line/70 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-red-700/80 hover:bg-red-50"
      >
        <X size={11} strokeWidth={2.6} />
        Remove pin
      </button>
    </div>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={[
        "rounded-full px-2 py-0.5 text-[10.5px] font-medium transition-all",
        active
          ? "bg-heda-ink text-white shadow-sm"
          : "bg-heda-cream/70 text-heda-mute hover:bg-heda-cream hover:text-heda-ink",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
